/**
 * User-to-user direct messaging (DM) API wrapper.
 *
 * All REST calls go through axiosInstance (Bearer + tenant headers injected).
 * Attachments are auth-protected binaries — they must be fetched as blobs via
 * axios (a bare <img src> would miss the Authorization header).
 *
 * Realtime updates arrive over a single app-wide WebSocket — see
 * src/hooks/useMessagingSocket.ts. Cross-component sync uses the DM_EVENTS
 * window CustomEvents plus the DM_QUERY_KEYS react-query keys.
 */
import axiosInstance from '../api/axiosInstance';
import { buildQueryString } from '../utils/query';
import { MESSAGING_ENDPOINTS } from '@/api/endpoints/messaging.endpoints';

// ── Types (mirror the backend contract) ─────────────────────────────────────

export interface DmUser {
  id: number;
  name: string;
  username: string;
  avatar_url: string | null;
}

export interface DmAttachment {
  message_id: number;
  filename: string;
  content_type: string;
  size: number;
  is_image: boolean;
}

export interface DmMessage {
  id: number;
  thread_id: number;
  sender_id: number;
  body: string;
  created_at: string;
  attachment: DmAttachment | null;
  /** Frontend-only: optimistic message not yet confirmed by the server. */
  pending?: boolean;
  /** Frontend-only: object URL for previewing a not-yet-uploaded image. */
  local_preview_url?: string | null;
}

export interface DmThreadLastMessage {
  id: number;
  body_preview: string;
  sender_id: number;
  created_at: string;
  has_attachment: boolean;
}

export interface DmThread {
  id: number;
  other_user: DmUser;
  last_message: DmThreadLastMessage | null;
  unread_count: number;
  updated_at: string;
}

export interface DmThreadListResponse {
  items: DmThread[];
  total: number;
}

export interface DmMessageListResponse {
  /** Messages in ASCENDING chronological order. */
  items: DmMessage[];
  has_more: boolean;
}

export interface DmReadResponse {
  ok: boolean;
  last_read_at: string;
}

export interface DmUnreadCount {
  threads: number;
  messages: number;
}

// ── Shared react-query keys + window event names ────────────────────────────

export const DM_QUERY_KEYS = {
  threads: ['dm-threads'] as const,
  unread: ['dm-unread'] as const,
  /** Root key — matches every per-thread message cache. */
  messagesRoot: ['dm-messages'] as const,
  messages: (threadId: number) => ['dm-messages', threadId] as const,
};

export const DM_EVENTS = {
  NEW_MESSAGE: 'dm:new-message',
  THREAD_READ: 'dm:thread-read',
  TYPING: 'dm:typing',
} as const;

export interface DmNewMessageEventDetail {
  thread_id: number;
  message: DmMessage;
}

export interface DmThreadReadEventDetail {
  thread_id: number;
  user_id: number;
  last_read_at: string;
}

export interface DmTypingEventDetail {
  thread_id: number;
  user_id: number;
  name: string;
}

// ── REST wrapper ─────────────────────────────────────────────────────────────

export const MessagingApi = {
  /** Create (or return the existing) 1:1 thread with a user. */
  createThread: async (recipientId: number): Promise<DmThread> =>
    (
      await axiosInstance.post<DmThread>(MESSAGING_ENDPOINTS.THREADS, {
        recipient_id: recipientId,
      })
    ).data,

  listThreads: async (offset = 0, limit = 50): Promise<DmThreadListResponse> =>
    (
      await axiosInstance.get<DmThreadListResponse>(
        `${MESSAGING_ENDPOINTS.THREADS}${buildQueryString({ offset, limit })}`
      )
    ).data,

  listMessages: async (
    threadId: number,
    opts: { before_id?: number; limit?: number } = {}
  ): Promise<DmMessageListResponse> =>
    (
      await axiosInstance.get<DmMessageListResponse>(
        `${MESSAGING_ENDPOINTS.THREAD_MESSAGES(threadId)}${buildQueryString({
          before_id: opts.before_id,
          limit: opts.limit ?? 50,
        })}`
      )
    ).data,

  /** Multipart send — body may be '' when only an attachment is sent. */
  sendMessage: async (
    threadId: number,
    body: string,
    attachment?: File
  ): Promise<DmMessage> => {
    const form = new FormData();
    form.append('body', body ?? '');
    if (attachment) form.append('attachment', attachment);
    return (
      await axiosInstance.post<DmMessage>(
        MESSAGING_ENDPOINTS.THREAD_MESSAGES(threadId),
        form,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120_000, // uploads can outlive the 30s default
        }
      )
    ).data;
  },

  markRead: async (threadId: number): Promise<DmReadResponse> =>
    (await axiosInstance.post<DmReadResponse>(MESSAGING_ENDPOINTS.THREAD_READ(threadId))).data,

  unreadCount: async (): Promise<DmUnreadCount> =>
    (await axiosInstance.get<DmUnreadCount>(MESSAGING_ENDPOINTS.UNREAD_COUNT)).data,

  /** Auth-protected binary — must go through axios, never a bare <img src>. */
  downloadAttachment: async (messageId: number): Promise<Blob> =>
    (
      await axiosInstance.get<Blob>(MESSAGING_ENDPOINTS.ATTACHMENT(messageId), {
        responseType: 'blob',
        timeout: 120_000,
      })
    ).data,
};

// ── Attachment helpers ───────────────────────────────────────────────────────

/** Session-lived object-URL cache so inline images are fetched once. */
const attachmentUrlCache = new Map<number, string>();

export async function getAttachmentObjectUrl(messageId: number): Promise<string> {
  const cached = attachmentUrlCache.get(messageId);
  if (cached) return cached;
  const blob = await MessagingApi.downloadAttachment(messageId);
  const url = URL.createObjectURL(blob);
  attachmentUrlCache.set(messageId, url);
  return url;
}

/** Save a blob to disk with the given filename. */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'attachment';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser a beat to start the download before revoking.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function humanFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value >= 10 ? Math.round(value) : value.toFixed(1)} ${units[i]}`;
}
